import { useState } from 'react';
import Link from 'next/link';
import key from 'weak-key';
import { min2secs, isGiveAwayLink } from '~/utils/utils';
import VideoPreview from '../videoPreview/videoPreview';
import LinkTracker from '~/components/linkTracker/linkTracker';
import useIsMobile from '~/hooks/useIsMobile';
import YoutubeCarouselContent from '~/components/carouselContent/youtubeCarouselContent';

/**
 * Infinite Grid
 * @category Functional Component
 * @summary Displays an infinite grid.
 * @prop {object} content - The blocks content.
 * @return {Node} React node containing an infinite grid.
 */

const PAGER_COUNT = 8;
const YoutubeInfiniteGrid = props => {
  const { content } = props;
  const isMobile = useIsMobile();
  const [visibleCount, setVisibleCount] = useState(PAGER_COUNT);

  if (!content) return false;

  const items = (content?.items ?? []).filter(item => item?.path);
  const visibleItems = items.slice(0, visibleCount);
  const hasMore = items.length > visibleCount;
  return (
      <div className={ isMobile ? 'constrain constrain--nopadd' : 'constrain'}>
        { isMobile ?
          <YoutubeCarouselContent
            items={ items }
            title={ content.category }
            count={ content?.field_view?.count || content?.field_display_as_view?.count }
            hideTypeIcons
          />
          :
          <div className='inf-section'>
            <h2>{ content.category }</h2>
            <div className='inf-section__grid'>
              {visibleItems.map((item, i) => (
                <LinkTracker
                  key={`inf-item-${key(item)}`}
                  destinationUrl={ item.path }
                  linkCta={ item.title }
                  linkFormat='tile'
                  linkRegion={ content.id }
                  linkSubRegion={ i + 1 }
                >
                  <Link legacyBehavior href={ item.path } passHref>
                    <a data-autotrack='false' target={ isGiveAwayLink(item?.path) ? '_blank' : '_self'}>
                      <div className='inf-section__item' >
                        <div className='inf-section__img'>
                          { item.field_video_mpx_id && item.field_video_mpx_id.duration !== undefined &&
                            <div className='block-videos__duration'>
                              { min2secs(item.field_video_mpx_id.duration) }
                            </div>
                          }
                          { item?.field_image?.field_image &&
                            <VideoPreview item={ item }>
                              <picture>
                                <source media='(max-width: 768px)' srcSet={ item.field_image.field_image.sm.url } />
                                <img
                                  loading='lazy'
                                  className='img-full'
                                  src={ item.field_image.field_image.lg.url }
                                  width={ item.field_image.field_image.lg.width }
                                  height={ item.field_image.field_image.lg.height }
                                  alt={ item.field_image?.alt }
                                />
                              </picture>
                            </VideoPreview>
                          }
                        </div>
                        <h4>
                          { item.title }
                        </h4>
                      </div>
                    </a>
                  </Link>
                </LinkTracker>
              ))}
            </div>
            { hasMore &&
              <div className='inf-section__caro-more-desktop'>
                <div className='text-center'>
                  <button
                    type='button'
                    onClick={() => setVisibleCount(prev => prev + PAGER_COUNT)}
                    aria-label='See more videos'
                    className='btn__cta'
                    data-autotrack='false'
                  >
                  <span>see more</span>
                  </button>
                </div>
              </div>
            }
          </div>
        }
      </div>
    );
}

export default YoutubeInfiniteGrid;
